import { Size } from '../enums/size.enum';
import { StepValues } from '../models/step-values';
import { ProductHandle } from '../../enums/ProductHandle.enum';
import {
  upsizeBraCup,
  downsizeBraCup,
  convertToBraObject,
  upsizeBraBand,
  downsizeBraBand,
  getSuggestedTankSizeFromBraSize,
  camiBodysuitBraSize,
  tankBodysuitBraSize,
  BraSize,
  getSuggestedCamiSizeFromBraSize,
} from './bra-helper';
import { getMeasurementSizeFromWaistHips } from './waist-hip-helper';
import { getOptionsFromStepValues } from './option-helper';
import { getBraModifications, getDressSizeForVneckBra } from './v-neck-bra-helper';
import { average, isDefined } from './array-helper';
import { ViewProduct } from '../../models/ViewProduct.model';
import { Fit } from '../../enums/Fit.enum';
import { calculateBST01Size } from './boldness-tank-helper';
import { calculateLeggingsSize } from '../constants/steps-metadata/leggings.steps';
import { calculateSculptwearSize } from './size-calculator-sculptwear';
import { calculateWaistHeroSize } from './size-calculator-waisthero';

export const calculateFitFromStepValues = (stepValues: StepValues, product: ViewProduct | undefined): Fit | undefined => {
  if (!product?.fits?.length) {
    return undefined;
  }
  const height = stepValues.get('height')?.metricText;
  const heightCm = height && parseInt(height, 10);

  return heightCm && heightCm <= 157 ? Fit.Petite : Fit.Regular;
};

export const calculateSizeFromStepValues = (stepValues: StepValues, productHandle: ProductHandle | undefined, withModifiers = true): Size => {
  if (stepValues.get('not-ready-s')?.value?.toUpperCase() === 'ORDER IN SIZE SMALL') {
    return Size.S;
  }

  if (stepValues.get('not-ready')?.value?.toUpperCase() === 'ORDER IN SIZE 3X') {
    return Size['3X'];
  }

  if (stepValues.get('not-ready')?.value?.toUpperCase() === 'ORDER IN SIZE 4X') {
    return Size['4X'];
  }

  if (withModifiers) {
    const resultsOption = stepValues.get('results');
    if (resultsOption?.value) {
      const ret = Size[resultsOption.value as any] as unknown as Size;
      return ret;
    }
  }

  const options = getOptionsFromStepValues(stepValues);

  switch (productHandle) {
    case ProductHandle.WaistHeroCincher:
      return calculateWaistHeroSize(options.dress, options.dressOther, options.waist, options.bra);
    case ProductHandle.BoldnessTank:
    case ProductHandle.BoldnessBodysuit:
      return calculateBST01Size(options.waist, options.bra);
    case ProductHandle.SilhouetteBra:
    case ProductHandle.CrossoverBra:
    case ProductHandle.VNeckBra:
      return calculateBraSize({ ...options, handle: productHandle });
    case ProductHandle.LiftWearTank:
      return calculateTankSize(options.bra, options.braAdditional, options.hook, options.fittedTops, options.dress, options.kindOfFit);
    case ProductHandle.LiftWearCami:
      return calculateCamiSize(options.bra, options.braAdditional, options.hook, options.fittedTops, options.dress, options.kindOfFit);
    case ProductHandle.EverydayCottonThong:
    case ProductHandle.EverydayCottonBrief:
      return calculateEverydayUnderwearSize(options.hips, options.pants);
    case ProductHandle.SilhouetteBrief:
      return calculateEverydayUnderwearSize(options.hips, options.pants);
    case ProductHandle.CamiBodysuit:
      return calculateCamiBodysuitSize(options.bra, options.waist, options.height, Size.XS, Size['4X']);
    case ProductHandle.TankBodysuit:
      return calculateTankBodysuitSize(options.bra, options.waist, options.height, Size.XS, Size['3X']);
    case ProductHandle.LeggingTwo:
    case ProductHandle.EverReadyPant:
      return calculateLeggingsSize(options.waist, options.hips, options.pants);
    case ProductHandle.SuperPowerShort:
    case ProductHandle.SuperPowerShortV2:
    case ProductHandle.SuperPowerBrief:
    case ProductHandle.SuperPowerThong:
    case ProductHandle.QueenBrief:
    default:
      return calculateSculptwearSize(options.dress, options.dressOther, options.waist, options.bra);
  }
};

const safeSize = (size: Size, minSize: Size | undefined, maxSize: Size | undefined): Size => {
  if (minSize && size < minSize) {
    return minSize;
  }

  if (maxSize && size > maxSize) {
    return maxSize;
  }

  return size;
};

const camiBodysuitWaistChart: { [waist: number]: Size | undefined } = {
  25: Size.XS,
  26: Size.XS,
  27: Size.S,
  28: Size.S,
  29: Size.S,
  30: Size.M,
  31: Size.M,
  32: Size.M,
  33: Size.L,
  34: Size.L,
  35: Size.L,
  36: Size.XL,
  37: Size.XL,
  38: Size.XL,
  39: Size['1X'],
  40: Size['1X'],
  41: Size['1X'],
  42: Size['2X'],
  43: Size['2X'],
  44: Size['2X'],
  45: Size['3X'],
  46: Size['3X'],
  47: Size['3X'],
  48: Size['4X'],
  49: Size['4X'],
  50: Size['4X'],
  51: Size['4X'],
  52: Size['4X'],
  53: Size['4X'],
  54: Size['4X'],
  55: Size['4X'],
  56: Size['4X'],
  57: Size['4X'],
};

const tankBodysuitWaistChart: { [waist: number]: Size | undefined } = {
  25: Size.XS,
  26: Size.XS,
  27: Size.S,
  28: Size.S,
  29: Size.S,
  30: Size.M,
  31: Size.M,
  32: Size.M,
  33: Size.L,
  34: Size.L,
  35: Size.L,
  36: Size.XL,
  37: Size.XL,
  38: Size.XL,
  39: Size['1X'],
  40: Size['1X'],
  41: Size['1X'],
  42: Size['2X'],
  43: Size['2X'],
  44: Size['2X'],
  45: Size['3X'],
  46: Size['3X'],
  47: Size['3X'],
  48: Size['4X'],
  49: Size['4X'],
  50: Size['4X'],
};

const calculateTankSize = (
  bra: BraSize | undefined,
  braAdditional: BraSize | undefined,
  hook: string | undefined,
  fittedTops: string | undefined,
  dress = -1,
  kindOfFit: string | undefined,
  minSize?: Size,
  maxSize?: Size,
): Size => {
  // console.log('Calculating size for tank', { bra, braAdditional, hook, fittedTops, dress, kindOfFit, modifier });

  const safeTankSize = (size: Size): Size => {
    return safeSize(size, minSize, maxSize);
  };

  const applicableSizes: Size[] = [];

  if (dress < 0) {
    // do nothing
  } else if (dress <= 2) {
    applicableSizes.push(safeTankSize(Size.XS));
  } else if (dress <= 6) {
    applicableSizes.push(safeTankSize(Size.S));
  } else if (dress <= 10) {
    applicableSizes.push(safeTankSize(Size.M));
  } else if (dress <= 14) {
    applicableSizes.push(safeTankSize(Size.L));
  } else if (dress <= 16) {
    applicableSizes.push(safeTankSize(Size.XL));
  } else if (dress <= 18) {
    applicableSizes.push(safeTankSize(Size['1X']));
  } else if (dress <= 20) {
    applicableSizes.push(safeTankSize(Size['2X']));
  } else {
    applicableSizes.push(safeTankSize(Size['3X']));
  }

  const braSizeResult: Size | undefined = bra ? getSuggestedTankSizeFromBraSize(bra) : undefined;
  const additionalBraSizeResult: Size | undefined = braAdditional ? getSuggestedTankSizeFromBraSize(braAdditional) : undefined;

  if (braSizeResult !== undefined) {
    applicableSizes.push(braSizeResult);

    if (additionalBraSizeResult !== undefined && additionalBraSizeResult !== braSizeResult) {
      applicableSizes.push(additionalBraSizeResult);
    }
  }

  if (applicableSizes.length === 0) {
    return Size['?'];
  }

  let fittedTopResult: Size | undefined;
  if (fittedTops) {
    switch (fittedTops.toUpperCase()) {
      case 'XS':
        fittedTopResult = safeTankSize(Size.XS);
        break;
      case 'S':
        fittedTopResult = safeTankSize(Size.S);
        break;
      case 'M':
        fittedTopResult = safeTankSize(Size.M);
        break;
      case 'L':
        fittedTopResult = safeTankSize(Size.L);
        break;
      case 'XL':
        fittedTopResult = safeTankSize(Size.XL);
        break;
      case '1X':
        fittedTopResult = safeTankSize(Size['1X']);
        break;
      case '2X':
        fittedTopResult = safeTankSize(Size['2X']);
        break;
      case '3X':
        fittedTopResult = safeTankSize(Size['3X']);
        break;
      // case '4x':
      // case '5x':
      default:
        break;
    }

    if (fittedTopResult !== undefined) {
      applicableSizes.push(fittedTopResult);
    }
  }

  const sum = applicableSizes.reduce((a, b) => a + b, 0);
  const applicableSizeAverage = sum / applicableSizes.length;
  const roundedAverage = Math.round(applicableSizeAverage);
  const mid = Math.floor(applicableSizes.length / 2);
  const sortedSizes = [...applicableSizes].sort((a, b) => a - b);
  const median = sortedSizes[mid];

  const finalSize = Math.max(roundedAverage, median);

  // console.log({
  //   applicableSizes,
  //   average,
  //   roundedAverage,
  //   averageSize: Size[roundedAverage],
  //   medianSize: Size[median],
  //   maxSize: Size[Math.max(roundedAverage, median)],
  //   finalSize: Size[finalSize],
  // });

  return finalSize;
};

const calculateCamiSize = (
  bra: BraSize | undefined,
  braAdditional: BraSize | undefined,
  hook: string | undefined,
  fittedTops: string | undefined,
  dress = -1,
  kindOfFit: string | undefined,
  minSize?: Size,
  maxSize?: Size,
): Size => {
  // console.log('Calculating size for tank', { bra, braAdditional, hook, fittedTops, dress, kindOfFit, modifier });

  const safeCamiSize = (size: Size): Size => {
    return safeSize(size, minSize, maxSize);
  };

  const applicableSizes: Size[] = [];

  if (dress < 0) {
    // do nothing
  } else if (dress <= 2) {
    applicableSizes.push(safeCamiSize(Size.XS));
  } else if (dress <= 6) {
    applicableSizes.push(safeCamiSize(Size.S));
  } else if (dress <= 10) {
    applicableSizes.push(safeCamiSize(Size.M));
  } else if (dress <= 14) {
    applicableSizes.push(safeCamiSize(Size.L));
  } else if (dress <= 16) {
    applicableSizes.push(safeCamiSize(Size.XL));
  } else if (dress <= 18) {
    applicableSizes.push(safeCamiSize(Size['1X']));
  } else if (dress <= 20) {
    applicableSizes.push(safeCamiSize(Size['2X']));
  } else if (dress <= 24) {
    applicableSizes.push(safeCamiSize(Size['3X']));
  } else {
    applicableSizes.push(safeCamiSize(Size['4X']));
  }

  const braSizeResult: Size | undefined = bra ? getSuggestedCamiSizeFromBraSize(bra) : undefined;
  const additionalBraSizeResult: Size | undefined = braAdditional ? getSuggestedCamiSizeFromBraSize(braAdditional) : undefined;

  if (braSizeResult !== undefined && braSizeResult !== Size['?']) {
    applicableSizes.push(braSizeResult);

    if (additionalBraSizeResult !== undefined && additionalBraSizeResult !== braSizeResult && additionalBraSizeResult !== Size['?']) {
      applicableSizes.push(additionalBraSizeResult);
    }
  }

  if (applicableSizes.length === 0) {
    return Size['?'];
  }

  let fittedTopResult: Size | undefined;
  if (fittedTops) {
    switch (fittedTops.toUpperCase()) {
      case 'XS':
        fittedTopResult = safeCamiSize(Size.XS);
        break;
      case 'S':
        fittedTopResult = safeCamiSize(Size.S);
        break;
      case 'M':
        fittedTopResult = safeCamiSize(Size.M);
        break;
      case 'L':
        fittedTopResult = safeCamiSize(Size.L);
        break;
      case 'XL':
        fittedTopResult = safeCamiSize(Size.XL);
        break;
      case '1X':
        fittedTopResult = safeCamiSize(Size['1X']);
        break;
      case '2X':
        fittedTopResult = safeCamiSize(Size['2X']);
        break;
      case '3X':
        fittedTopResult = safeCamiSize(Size['3X']);
        break;
      case '4X':
        fittedTopResult = safeCamiSize(Size['4X']);
        break;
      // case '5x':
      default:
        break;
    }

    if (fittedTopResult !== undefined) {
      applicableSizes.push(fittedTopResult);
    }
  }

  const sum = applicableSizes.reduce((a, b) => a + b, 0);
  const applicableSizeAverage = sum / applicableSizes.length;
  const roundedAverage = Math.round(applicableSizeAverage);
  const mid = Math.floor(applicableSizes.length / 2);
  const sortedSizes = [...applicableSizes].sort((a, b) => a - b);
  const median = sortedSizes[mid];

  const finalSize = Math.max(roundedAverage, median);

  // console.log({
  //   applicableSizes,
  //   average,
  //   roundedAverage,
  //   averageSize: Size[roundedAverage],
  //   medianSize: Size[median],
  //   maxSize: Size[Math.max(roundedAverage, median)],
  //   finalSize: Size[finalSize],
  // });

  return finalSize;
};

const calculateBraSize = ({
  bra,
  braAdditional,
  fittedShirts,
  cupFit,
  bandFit,
  dress,
  handle,
  minSize,
  maxSize,
}: {
  bra: BraSize | undefined;
  braAdditional: BraSize | undefined;
  hook: string | undefined;
  fittedShirts: string | undefined;
  kindOfFit: string | undefined;
  cupFit: string | undefined;
  bandFit: string | undefined;
  dress: number | undefined;
  handle: ProductHandle;
  minSize?: Size;
  maxSize?: Size;
}): Size => {
  const safeBraSize = (size: Size) => safeSize(size, minSize, maxSize);
  // console.log('Calculating size for vneck bra', { bra, braAdditional, dress });

  const sizeBasedOnDress = getDressSizeForVneckBra(dress);

  const braModifications = getBraModifications({
    spillingOutCupUpModifier: cupFit?.toLowerCase() === 'i am spilling out of the cup',
    gappingCupDownModifier: cupFit?.toLowerCase() === 'there is gapping or empty space in my cup',
    tightBandUpModifier: bandFit?.toLowerCase() === 'my band is tight or digging in',
    bandRidingUpDownModifier: bandFit?.toLowerCase() === 'my band is riding up in the back',
  });

  const modifiedBraCupSize = braModifications.shouldUpsizeCup
    ? upsizeBraCup(bra)?.cup
    : braModifications.shouldDownsizeCup
    ? downsizeBraCup(bra)?.cup
    : convertToBraObject(bra)?.cup;

  const modifiedAdditionalBraCupSize = braModifications.shouldUpsizeCup
    ? upsizeBraCup(braAdditional)?.cup
    : braModifications.shouldDownsizeCup
    ? downsizeBraCup(braAdditional)?.cup
    : convertToBraObject(braAdditional)?.cup;

  const modifiedBraBandSize = braModifications.shouldUpsizeBand
    ? upsizeBraBand(bra, handle)?.band
    : braModifications.shouldDownsizeBand
    ? downsizeBraBand(bra, handle)?.band
    : convertToBraObject(bra)?.band;

  const modifiedAdditionalBraBandSize = braModifications.shouldUpsizeBand
    ? upsizeBraBand(braAdditional, handle)?.band
    : braModifications.shouldDownsizeBand
    ? downsizeBraBand(braAdditional, handle)?.band
    : convertToBraObject(braAdditional)?.band;

  const modifiedBraSize: BraSize | undefined = modifiedBraBandSize && modifiedBraCupSize ? `${modifiedBraBandSize}${modifiedBraCupSize}` : bra;
  const modifiedAdditionalBraSize: BraSize | undefined =
    modifiedAdditionalBraBandSize && modifiedAdditionalBraCupSize ? `${modifiedAdditionalBraBandSize}${modifiedAdditionalBraCupSize}` : undefined;

  const sizeBasedOnBra: Size | undefined = modifiedBraSize ? getSuggestedTankSizeFromBraSize(modifiedBraSize) : undefined;
  const sizeBasedOnAdditionalBra: Size | undefined = modifiedAdditionalBraSize ? getSuggestedTankSizeFromBraSize(modifiedAdditionalBraSize) : undefined;

  let fittedTopResult: Size | undefined;
  if (fittedShirts) {
    switch (fittedShirts.toUpperCase()) {
      case 'XS':
        fittedTopResult = Size.XS;
        break;
      case 'S':
        fittedTopResult = Size.S;
        break;
      case 'M':
        fittedTopResult = Size.M;
        break;
      case 'L':
        fittedTopResult = Size.L;
        break;
      case 'XL':
        fittedTopResult = Size.XL;
        break;
      case '1X':
        fittedTopResult = Size['1X'];
        break;
      case '2X':
        fittedTopResult = Size['2X'];
        break;
      case '3X':
        fittedTopResult = Size['3X'];
        break;
      // case '4x':
      // case '5x':
      default:
        break;
    }
  }

  const applicableSizes = [sizeBasedOnDress, sizeBasedOnBra, sizeBasedOnAdditionalBra, fittedTopResult].filter(isDefined).map((s) => safeBraSize(s));

  if (applicableSizes.length > 0) {
    const sum = applicableSizes.reduce((a, b) => a + b, 0);
    const applicableSizeAverage = sum / applicableSizes.length;
    const roundedAverage = Math.round(applicableSizeAverage);
    const mid = Math.floor(applicableSizes.length / 2);
    const sortedSizes = [...applicableSizes].sort((a, b) => a - b);
    const median = sortedSizes[mid];
    const finalSize = Math.max(roundedAverage, median);

    // console.log({
    //   bra,
    //   modifiedBraCupSize,
    //   modifiedBraBandSize,
    //   braAdditional,
    //   modifiedAdditionalBraCupSize,
    //   modifiedAdditionalBraBandSize,
    //   applicableSizes,
    //   braModifications,
    //   average,
    //   roundedAverage,
    //   averageSize: Size[roundedAverage],
    //   medianSize: Size[median],
    //   maxSize: Size[Math.max(roundedAverage, median)],
    //   finalSize: Size[finalSize],
    // });

    return finalSize;
  }

  return Size['?'];
};

const getEverydayUnderwearMeasurementSizeFromHips = (hips: number): Size => {
  // XS		35-36
  // S		37-39
  // M		40-42
  // L		43-45
  // XL		46-47
  // 1X		48-51
  // 2X		52-54
  // 3X		55-59
  // 4X		60-61 <- Currently not supported

  switch (true) {
    case hips <= 36:
      return Size.XS;
    case hips <= 39:
      return Size.S;
    case hips <= 42:
      return Size.M;
    case hips <= 45:
      return Size.L;
    case hips <= 47:
      return Size.XL;
    case hips <= 51:
      return Size['1X'];
    case hips <= 54:
      return Size['2X'];
    default:
      return Size['3X'];
  }
};

const getEverydayUnderwearMeasurementSizeFromPants = (pants: number): Size => {
  // Pant Size
  // XS	2
  // S	4-6
  // M	8-10
  // L	12
  // XL	14
  // 1XL	16
  // 2XL	18-20
  // 3XL	22-24
  // 4X	26 <-- Currently not supported

  switch (true) {
    case pants <= 2:
      return Size.XS;
    case pants <= 6:
      return Size.S;
    case pants <= 10:
      return Size.M;
    case pants <= 12:
      return Size.L;
    case pants <= 14:
      return Size.XL;
    case pants <= 16:
      return Size['1X'];
    case pants <= 20:
      return Size['2X'];
    default:
      return Size['3X'];
  }
};

const calculateCamiBodysuitSize = (bra: BraSize | undefined, waist = -1, height: string | undefined, minSize?: Size, maxSize?: Size): Size => {
  const braSize = bra && camiBodysuitBraSize(bra);
  if (typeof braSize !== 'number') {
    return Size['?'];
  }

  const waistSize = waist && camiBodysuitWaistChart[waist];
  const sizeBasedOnBraAndWaist = waistSize ? Math.min(waistSize, braSize) : braSize;

  return safeSize(sizeBasedOnBraAndWaist, minSize, maxSize);
};

const calculateTankBodysuitSize = (bra: BraSize | undefined, waist = -1, height: string | undefined, minSize?: Size, maxSize?: Size): Size => {
  const braSize = bra && tankBodysuitBraSize(bra);
  if (typeof braSize !== 'number') {
    return Size['?'];
  }

  const waistSize = waist && tankBodysuitWaistChart[waist];
  const sizeBasedOnBraAndWaist = waistSize ? Math.min(waistSize, braSize) : braSize;

  return safeSize(sizeBasedOnBraAndWaist, minSize, maxSize);
};

const calculateEverydayUnderwearSize = (hips: number | undefined, pants: number | undefined, minSize?: Size, maxSize?: Size): Size => {
  const measurementSizeFromHips = (hips && getEverydayUnderwearMeasurementSizeFromHips(hips)) ?? undefined;
  const measurementSizeFromPants = (pants && getEverydayUnderwearMeasurementSizeFromPants(pants)) ?? undefined;

  const applicableSafeSizes = [measurementSizeFromHips, measurementSizeFromPants].filter(isDefined).map((s) => safeSize(s, minSize, maxSize));
  const avg = average(applicableSafeSizes);
  const roundedAvg = Math.floor(avg);

  return roundedAvg;
};

export const replaceOutliersWithUndefinedFor3SizeArray = (rawSizes: (number | undefined)[]): (number | undefined)[] => {
  // If we don't have 3 sizes, no need to look for outliers
  if (rawSizes.includes(undefined)) return rawSizes;
  // Tell typescript we have all numbers now
  const sizes = rawSizes.filter(isDefined);

  // This algorithm only handles 3 sizes properly, supporting more will require refactoring
  if (sizes.length !== 3) {
    debugger; // This will force testers using dev tools to see this
  }

  const min = Math.min(...sizes);
  const max = Math.max(...sizes);

  // If the spread between the min and max is 2, remove the farthest outlier
  if (max - min > 2) {
    const minIndex = sizes.indexOf(min);
    const maxIndex = sizes.indexOf(max);
    const otherIndex = Array.from({ length: sizes.length })
      .map((_, i) => i)
      .findIndex((_, i) => i !== minIndex && i !== maxIndex);

    if (sizes[maxIndex] - sizes[otherIndex] > sizes[otherIndex] - sizes[minIndex]) {
      console.log('Max was outlier:', JSON.stringify({ before: sizes, after: sizes.map((s, i) => (i === maxIndex ? undefined : s)) }, null, 2));
      return sizes.map((s, i) => (i === maxIndex ? undefined : s));
    } else {
      console.log('Min was outlier:', JSON.stringify({ before: sizes, after: sizes.map((s, i) => (i === minIndex ? undefined : s)) }, null, 2));
      return sizes.map((s, i) => (i === minIndex ? undefined : s));
    }
  }

  console.log('No outliers detected', sizes);
  return sizes;
};
